<script lang="ts">
  import Price from "@retailer/svelte/Shared/Price.svelte";
  import FuturePricesStore from "@shared/svelte/Stores/FuturePricesStore.js";
  import PostOffPricing from "@retailer/svelte/ProductDetails/Components/PostOffPricing.svelte";
  import { getDataForInventory } from "@retailer/services/pricing_data_service";
  import { get as getStoreValue } from "svelte/store";
  import { onMount } from "svelte";
  import LoadingSpinner from "@shared/svelte/Components/LoadingSpinner.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import type { Product, ProductLine } from "@shared/types/ProductLine";
  import type { InventoryPrice } from "@shared/types/ProductLine";
  import type { PricingDataResult } from "@shared/types/Pricing";
  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";

  export let inventoryId: number;
  export let productTypeId: number;
  export let trackingEntities: BaseEntityClass[] = [];
  export let hideCustomPrice: boolean = false;
  export let showPricePerOunce: boolean = true;
  export let onFetchComplete: (prices: InventoryPrice) => void = _ => {};
  export let warehouseLocationCode: string | null = null;

  const productLine = { product_type_id: productTypeId } as ProductLine;
  const { trackEvent } = EventTracking;

  let isLoading = true;
  let priceData: InventoryPrice;

  const trackPricesDisplayed = (response: InventoryPrice) => {
    const hasPrices = response?.case_price || response?.unit_price;
    if (hasPrices && trackingEntities.length > 0) {
      trackEvent("prices_displayed", trackingEntities);
    }
  };

  const trackPricesFailedToLoad = () => {
    if (trackingEntities.length > 0) {
      trackEvent("prices_load_failed", trackingEntities);
    }
  };

  const onSuccess = (response: InventoryPrice) => {
    priceData = response;
    isLoading = false;
    trackPricesDisplayed(response);
    onFetchComplete(response);
  };

  const onFailure = () => {
    isLoading = false;
    trackPricesFailedToLoad();
  };

  onMount(async () => {
    const result: PricingDataResult = await getDataForInventory(
      inventoryId,
      onFailure,
      warehouseLocationCode,
    );

    const futurePrices = result?.future_price || [];
    FuturePricesStore.update(n => {
      n.push(futurePrices);
      return n;
    });
    const price = result?.inventory_price;
    if (price) {
      onSuccess(price);
    } else {
      isLoading = false;
    }
    await InventoryItemStore.fetchInventoryItem(inventoryId);
  });

  const stateSettings = getStoreValue(StateSettingsStore);
  const usesFuturePricingWithMinBottle = stateSettings
    ? stateSettings["uses_future_pricing_with_min_bottle"]
    : false;
  const usesFuturePricing = stateSettings ? stateSettings["uses_future_pricing"] : false;
  const showFuturePricing = usesFuturePricing || usesFuturePricingWithMinBottle;
  const showPostOffPricing = usesFuturePricingWithMinBottle && showFuturePricing;

  $: product = priceData as Product;
  $: showPrice =
    priceData &&
    (priceData.case_price || priceData.unit_price) &&
    (!priceData.has_custom_price || (priceData.has_custom_price && !hideCustomPrice));
</script>

{#if isLoading}
  <LoadingSpinner />
{:else if showPostOffPricing && product}
  <PostOffPricing {product} {productLine} {showPricePerOunce} />
{:else if showPrice}
  <div>
    {#if priceData.has_custom_price}
      <p>Custom Price:</p>
    {/if}
    <div class="fizz-text-medium fizz-text-strong">
      <Price {product} {productLine} {showPricePerOunce} />
    </div>
  </div>
{:else}
  <slot name="placeholder" />
{/if}
